import { useState, type FormEvent } from 'react';
import { Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/api/errors';
import { SHARE_ROLES, useGrantShare, type ShareRole } from '@/api/projects';
import { brand } from '@/theme/theme';

export interface ShareModalProps {
  projectId: string;
  opened: boolean;
  onClose: () => void;
}

export function ShareModal({ projectId, opened, onClose }: ShareModalProps) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation();
  const grant = useGrantShare(projectId);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ShareRole>('viewer');
  const [expires, setExpires] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setEmail('');
    setRole('viewer');
    setExpires('');
    setError(null);
    onClose();
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    try {
      await grant.mutateAsync({ email, role, expires_at: expires ? new Date(expires + 'T23:59:59').toISOString() : undefined });
      handleClose();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t('share.add')}>
      <form onSubmit={(e) => void handleSubmit(e)}>
        <Stack gap="md">
          <TextInput label={t('share.email')} type="email" required withAsterisk={false} value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
          <Select
            label={t('share.role')}
            data={SHARE_ROLES.map((r) => ({ value: r, label: t(`share.roles.${r}`) }))}
            value={role}
            allowDeselect={false}
            onChange={(v) => v && setRole(v as ShareRole)}
          />
          {role === 'uploader' && (
            <Text fz={12} c={brand.textMuted}>
              {t('share.uploaderHint')}
            </Text>
          )}
          <TextInput label={t('share.expires')} type="date" value={expires} onChange={(e) => setExpires(e.currentTarget.value)} />
          {error && (
            <Text c="red" role="alert">
              {error}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              {tc('common.cancel')}
            </Button>
            <Button type="submit" loading={grant.isPending}>
              {t('share.add')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
